import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  MapPin, Clock, Plus, Play, Trash2,
  Droplets, Zap, KeyRound, Hammer, Paintbrush, Ruler,
  LayoutGrid, Wind, Layers, Wrench, HelpCircle, Loader2,
} from "lucide-react";
import { ANNONCE_CATEGORIES } from "@/lib/constants";
import { getCategoryColors } from "@/lib/categoryColors";
import { cn } from "@/lib/utils";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useSEO } from "@/hooks/useSEO";
import { useMyAnnonces, useUpdateAnnonceStatus, useDeleteAnnonce, getUrgencyLabel } from "@/hooks/useAnnonces";
import { useEffect, useState } from "react";
import { toast } from "@/hooks/use-toast";
import ArtisanBottomNav from "@/components/ArtisanBottomNav";
import ClientBottomNav from "@/components/ClientBottomNav";
import { useLanguage } from "@/hooks/useLanguage";
import { useLocalizedNavigate, useLocalizedPath } from "@/hooks/useLocalizedNavigate";
import { DirectionalArrow } from "@/components/DirectionalArrow";

const categoryIcons: Record<string, React.ElementType> = {
  plumbing: Droplets, electricity: Zap, locksmith: KeyRound,
  masonry: Hammer, painting: Paintbrush, carpentry: Ruler,
  plaquiste: Layers, pvc_aluminium: Wrench,
  tiling: LayoutGrid, hvac: Wind, insulation: Layers,
  all_trades: Wrench, other: HelpCircle,
};


const MesAnnonces = () => {
  useSEO({ noindex: true });
  const { t, locale } = useLanguage("client");
  const lNavigate = useLocalizedNavigate();
  const lp = useLocalizedPath();
  const { user, loading: authLoading } = useAuth();
  const [categoryFilter, setCategoryFilter] = useState("all");
  const { data: annonces = [], isLoading } = useMyAnnonces(categoryFilter);
  const updateStatus = useUpdateAnnonceStatus();
  const deleteAnnonce = useDeleteAnnonce();

  useEffect(() => {
    if (!authLoading && !user) lNavigate("/connexion");
  }, [user, authLoading, lNavigate]);

  const handleStatusChange = (id: string, newStatus: string) => {
    updateStatus.mutate({ id, status: newStatus }, {
      onSuccess: () => toast({ title: t("mes_annonces.toast_status_ok") }),
      onError: (err: any) => toast({ title: t("mes_annonces.toast_error"), description: err.message, variant: "destructive" }),
    });
  };

  const handleDelete = (id: string) => {
    deleteAnnonce.mutate(id, {
      onSuccess: () => toast({ title: t("mes_annonces.toast_deleted") }),
      onError: (err: any) => toast({ title: t("mes_annonces.toast_error"), description: err.message, variant: "destructive" }),
    });
  };

  if (authLoading) return (
    <div className="min-h-screen">
      <Navbar />
      <main className="container flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </main>
    </div>
  );

  const count = annonces.length;
  const countLabel = count === 1
    ? t("mes_annonces.count_one")
    : t("mes_annonces.count_other", { count });

  const statusColors: Record<string, string> = {
    published: "bg-success text-success-foreground",
    paused: "bg-warning text-warning-foreground",
    closed: "bg-muted text-muted-foreground",
    draft: "bg-muted text-muted-foreground",
    archived: "bg-muted text-muted-foreground",
  };

  const statusLabel = (s: string) => {
    const map: Record<string, string> = {
      published: t("mes_annonces.status_published"),
      paused: t("mes_annonces.status_paused"),
      closed: t("mes_annonces.status_closed"),
      draft: t("mes_annonces.status_draft"),
      archived: t("mes_annonces.status_archived"),
    };
    return map[s] ?? map["draft"];
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="container py-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="font-display text-3xl font-bold">{t("mes_annonces.title")}</h1>
            <p className="mt-1 text-muted-foreground">{countLabel}</p>
          </div>
          <Link to={lp("/poster-annonce")}>
            <Button variant="hero">
              <Plus className="me-2 h-4 w-4" /> {t("mes_annonces.new")}
            </Button>
          </Link>
        </div>

        <div className="mb-6">
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[220px]">
              <SelectValue placeholder={t("mes_annonces.filter_label")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("mes_annonces.filter_all")}</SelectItem>
              {ANNONCE_CATEGORIES.map((cat) => (
                <SelectItem key={cat.value} value={cat.value}>{locale === "ar" ? cat.label_ar : cat.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {isLoading ? (
          <div className="grid gap-4 md:grid-cols-2">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="overflow-hidden border-l-4 border-l-muted">
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-start justify-between">
                    <Skeleton className="h-5 w-40" />
                    <Skeleton className="h-5 w-20 rounded-full" />
                  </div>
                  <Skeleton className="h-4 w-28" />
                  <Skeleton className="h-4 w-32" />
                  <div className="flex gap-2 pt-1">
                    <Skeleton className="h-8 w-24 rounded-md" />
                    <Skeleton className="h-8 w-24 rounded-md" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : annonces.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">{t("mes_annonces.empty")}</p>
              <Link to={lp("/poster-annonce")}>
                <Button variant="hero" className="mt-4">
                  <Plus className="me-2 h-4 w-4" /> {t("mes_annonces.empty_cta")}
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {annonces.map((annonce) => {
              const catCode = annonce.category ?? "other";
              const CatIcon = categoryIcons[catCode] ?? HelpCircle;
              const catColors = getCategoryColors(catCode);
              const catLabel = t(`annonces:categories_all.${catCode}`, {
                defaultValue: t("annonces:categories_all.other", { defaultValue: "Autre" }),
              });
              const isUrgent = annonce.urgency === "asap";
              const st = statusColors[annonce.status] ?? statusColors.draft;

              return (
                <Card
                  key={annonce.id}
                  className={cn("overflow-hidden border-s-4", catColors.border, isUrgent && "bg-orange-50/40 dark:bg-orange-950/10")}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="space-y-2 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={cn("inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium border", catColors.badgeBg, catColors.badgeText)}>
                            <CatIcon className="h-3 w-3" />
                            {catLabel}
                          </span>
                          {annonce.status !== "published" && (
                            <Badge className={`text-xs ${st}`}>{statusLabel(annonce.status)}</Badge>
                          )}
                        </div>
                        <h3 className="font-display text-lg font-semibold leading-tight">{annonce.title}</h3>
                      </div>
                    </div>

                    <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <MapPin className="h-3.5 w-3.5" />
                        <span>{annonce.commune}, {annonce.wilaya}</span>
                      </div>
                      <div className={`flex items-center gap-1 ${isUrgent ? "text-orange-600 font-medium" : ""}`}>
                        <Clock className="h-3.5 w-3.5" />
                        <span>{getUrgencyLabel(annonce.urgency, locale)}</span>
                      </div>
                    </div>

                    <div className="mt-4 flex items-center justify-between border-t pt-3">
                      <div className="flex gap-1">
                        {(annonce.status === "closed" || annonce.status === "paused") && (
                          <Button size="sm" variant="outline" onClick={() => handleStatusChange(annonce.id, "published")}>
                            <Play className="me-1 h-3 w-3" /> {t("mes_annonces.reactivate")}
                          </Button>
                        )}
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive hover:bg-destructive/10 px-2">
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>{t("mes_annonces.delete_title")}</AlertDialogTitle>
                              <AlertDialogDescription>
                                {t("mes_annonces.delete_desc")}
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>{t("annonces:common.cancel", { defaultValue: "Annuler" })}</AlertDialogCancel>
                              <AlertDialogAction
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                onClick={() => handleDelete(annonce.id)}
                              >
                                {t("mes_annonces.delete")}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                      <Link to={lp(`/annonce/${annonce.id}`)}>
                        <Button variant="ghost" size="sm">
                          {t("mes_annonces.view")} <DirectionalArrow className="ms-1 h-4 w-4" />
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>
      <Footer />
      <ArtisanBottomNav />
      <ClientBottomNav />
    </div>
  );
};

export default MesAnnonces;
